import React, {
  useState, useEffect, useCallback,
} from 'react';
import {
  Button, Col, DatePicker, Row, Select,
  Spin, Table, Form, notification,
} from 'antd';
import { useNavigate } from 'react-router-dom';
import FilterIcon from 'mdi-react/FilterIcon';
import FilterRemoveIcon from 'mdi-react/FilterRemoveIcon';
import moment from 'moment/moment';
import './PageLogs.scss';
import {
  patch, get, post, ResponseWrapper,
  RequestWrapper, isSuperAgentError,
} from '../../../dataSource/requests';
import CustomPagination from '../../sharedComponents/customPagination/CustomPagination';
import { LogActions } from '../../../sharedTypes/LogTypes';

const PageLogs = (): JSX.Element => {
  const navigate = useNavigate();
  const [formInstance] = Form.useForm();

  const [loading, setLoading] = useState<boolean>(false);
  const [loadingExcel, setLoadingExcel] = useState<boolean>(false);
  const [userOptions, setUserOptions] = useState([]);
  const [users, setUsers] = useState([]);
  const [data, setData] = useState([]);
  const [paginationInfo, setPaginationInfo] = useState({
    maxSize: 30,
    numberOfPages: 1,
    pageNumber: 1,
    totalElements: 1,
  });

  const [actions, setActions] = useState<LogActions[]>([]);

  const columns = [
    {
      title: <div className="table-titles">Data/hora da ação</div>,
      dataIndex: 'actionDate',
      key: 'actionDate',
      render: (date: string) => (date ? moment(date).format('DD/MM/YYYY [às] HH:mm') : ''),
    },
    {
      title: <div className="table-titles">Ação</div>,
      dataIndex: 'action',
      key: 'action',

      render: (text: string, record:any): string => {
        if (record.additionalParameters) {
          return `${text} ${record.additionalParameters}`;
        }

        return text;
      },
    },
    {
      title: <div className="table-titles">ID</div>,
      dataIndex: 'userId',
      key: 'userId',
    },
    {
      title: <div className="table-titles">Usuário</div>,
      key: 'userName',
      dataIndex: 'userName',
    },
  ];

  const getFilterParams = useCallback(() => {
    const values = formInstance.getFieldsValue();
    const params: {
      userId?: number,
      type?: string,
      periodStart?: string,
      periodEnd?: string,
      action?:string
    } = {};

    if (values.cooperativeFilter !== undefined) {
      params.userId = values.cooperativeFilter;
    }

    if (values.userTypeFilter !== undefined) {
      params.type = values.userTypeFilter;
    }

    if (values.intervalFilter.length) {
      params.periodStart = moment(values.intervalFilter[0])
        .startOf('day').format('YYYY-MM-DDTHH:mm:ss.SSSZ');
      params.periodEnd = moment(values.intervalFilter[1])
        .endOf('day').format('YYYY-MM-DDTHH:mm:ss.SSSZ');
    }

    if (values.action) {
      params.action = values.action;
    }

    return params;
  }, [formInstance]);

  const fetchData = useCallback(async (page: number) => {
    const params = getFilterParams();

    setLoading(true);

    const url = '/log/listAll';
    let response: ResponseWrapper | undefined;
    let responseError: ResponseWrapper | undefined;
    let genericError: Error | undefined;

    const requestReference: RequestWrapper = patch({
      url,
      params,
      query: {
        page: page - 1,
      },

      frontConfigs: {
        navigateFunction: navigate,
      },
    });

    try {
      response = await requestReference;
    } catch (error) {
      if (isSuperAgentError(error)) {
        responseError = error.response;
      } else {
        genericError = error as Error;
      }
    }

    setLoading(false);

    switch (true) {
      case (response?.body?.code === 10000):
        // "All logs listed successfully", 10000
        setData(response?.body.logs);
        setPaginationInfo((previous) => {
          return ({
            ...previous,
            ...response?.body.paginationInfo,
          });
        });
        setLoading(false);
        break;

      case (responseError?.body?.code === -10000):
        // "Invalid list logs filters", -10000
        break;

      case (responseError?.body?.code === -4001):
        // "You must be authenticated to perform this operation", -4001
        break;

      case (responseError?.body?.code === -4003):
        // "You do not have permission to perform this operation", -4003
        break;

      case (genericError?.message?.includes('has been aborted')):
        // dont show anything when request is aborted
        break;

      default:
        notification.error({
          message: 'Erro',
          description: 'Ocorreu um erro ao buscar a listagem, tente novamente',
        });
        break;
    }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchActions = async (): Promise<void> => {
    const requestReference: RequestWrapper = get({
      url: '/log/listActions',

      frontConfigs: {
        navigateFunction: navigate,
      },
    });

    try {
      const response:any = await requestReference;

      setActions(response?.body?.actionOutputs);
    } catch (error) {
      notification.error({
        message: 'Erro',
        description: 'Erro ao carregar a listagem de ações',
      });
    }
  };

  useEffect(() => {
    const fetchTypesWithUsers = async (): Promise<void> => {
      let response: ResponseWrapper | undefined;
      let responseError: ResponseWrapper | undefined;
      let genericError: Error | undefined;

      const requestReference: RequestWrapper = get({
        url: '/role/findTypesWithUsers',

        frontConfigs: {
          navigateFunction: navigate,
        },
      });

      try {
        response = await requestReference;
      } catch (error) {
        if (isSuperAgentError(error)) {
          responseError = error.response;
        } else {
          genericError = error as Error;
        }
      }

      switch (true) {
        case (response?.body?.code === 3010): {
          // "Listed types with users successfully", 3010
          const newUsers = response?.body.types;

          const newUserOptions = newUsers
            .filter((v:any) => v.type === 'COOPERATIVE')
            .map((v:any) => v.users)
            .flat(1);

          setUsers(newUsers);
          setUserOptions(newUserOptions);
        } break;

        case (responseError?.body?.code === -4001):
          // "You must be authenticated to perform this operation", -4001
          break;

        case (responseError?.body?.code === -4003):
          // "You do not have permission to perform this operation", -4003
          break;

        case (genericError?.message?.includes('has been aborted')):
          // dont show anything when request is aborted
          break;

        default:
          notification.error({
            message: 'Erro',
            description: 'Erro ao carregar a listagem de usuários responsáveis',
          });
          break;
      }
    };

    fetchTypesWithUsers();
    fetchData(1);
    fetchActions();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return () => {
      setLoading(false);
    };
  }, []);

  const cleanFilterFields = (): void => {
    formInstance.setFieldsValue({
      cooperativeFilter: '',
      userTypeFilter: 'COOPERATIVE',
      intervalFilter: [moment().subtract(30, 'day'), moment()],
    });
  };

  const handleFilter = (): void => {
    fetchData(1);
  };

  const loadUsers = (type:any): void => {
    formInstance.setFieldsValue({ cooperativeFilter: '' });

    const newUserOptions = users
      .filter((v:any) => v.type === type)
      .map((v:any) => v.users)
      .flat(1) as any;

    setUserOptions(newUserOptions);
  };

  const exportExcel = async (): Promise<void> => {
    setLoadingExcel(true);
    const url = '/log/export/excel';
    const paramsFilter = getFilterParams();
    const params = {
      type: paramsFilter.type,
      userId: paramsFilter.userId,
      periodStart: paramsFilter.periodStart,
      periodEnd: paramsFilter.periodEnd,
      action: paramsFilter.action || null,
    };

    try {
      const requestReference: RequestWrapper = post({
        url,
        params,

        frontConfigs: {
          navigateFunction: navigate,
        },

        headers: {
          'Content-Type': 'application/json',
          Accept: '*/*',
        },

        responseType: 'blob',
      });

      const resp = await requestReference;

      if (resp.status === 200) {
        const filee = new Blob([resp.body], { type: 'xlsx' });

        const a = document.createElement('a');
        const downloadUrl = window.URL.createObjectURL(filee);

        const today = new Date();
        const date = `${today.getDate()}-${today.getMonth() + 1}-${today.getFullYear()}`;
        a.href = downloadUrl;
        a.download = `Logs do sistema ${date}.xlsx`;

        document.body.append(a);

        a.click();
        setLoadingExcel(false);
        setTimeout(() => {
          a.remove();
          window.URL.revokeObjectURL(downloadUrl);
        }, 0);
      }
    } catch (error) {
      setLoadingExcel(false);
      console.log(error);
    }
  };

  return (
    <div className="PageLogs">
      <Spin tip="Carregando..." spinning={loading}>
        <Form
          scrollToFirstError
          layout="vertical"
          form={formInstance}
          onFinish={handleFilter}
          initialValues={{
            userTypeFilter: 'COOPERATIVE',
            intervalFilter: [moment().subtract(30, 'day'), moment()],
          }}
        >
          <Row gutter={24} className="title-container">
            <Col xs={{ span: 24 }} md={{ span: 24 }}>
              <span className="page-title">Relatório de logs do sistema</span>
            </Col>
          </Row>

          <Row gutter={24} className="filters-container">
            <Col xs={{ span: 24 }} lg={{ span: 6 }}>
              <Form.Item label="Usuário" name="cooperativeFilter" rules={[]}>
                <Select
                  showSearch
                  allowClear
                  style={{ width: '100%' }}
                  placeholder="Usuário"
                  filterOption={(input, option): any => option?.['props']?.children
                    .toLowerCase()
                    .includes(input.toLowerCase())}
                >
                  {userOptions.map((v:any) => (
                    <Select.Option key={v.id} value={v.id}>
                      {v.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={{ span: 24 }} lg={{ span: 6 }}>
              <Form.Item
                label="Tipo de usuário"
                name="userTypeFilter"
                rules={[]}
              >
                <Select
                  showSearch
                  style={{ width: '100%' }}
                  placeholder="Selecione o tipo de usuário"
                  onChange={loadUsers}
                >
                  <Select.Option value="COOPERATIVE">COOPERADO</Select.Option>
                  <Select.Option value="COOPERCITRUS">
                    COOPERCITRUS
                  </Select.Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={{ span: 24 }} lg={{ span: 6 }}>
              <Form.Item
                label="Intervalo de tempo"
                name="intervalFilter"
                rules={
                  [
                    // rules: [{ required: true, message: 'O período é obrigatório' }],
                    // rules: [{ validator: this.validateDateRange }],
                  ]
                }
              >
                <DatePicker.RangePicker
                  style={{ width: '100%' }}
                  // placeholder="Intervalo de tempo"
                  format="DD/MM/YYYY"
                />
              </Form.Item>
            </Col>
            <Col xs={{ span: 24 }} lg={{ span: 6 }}>
              <Row>
                <Col xs={{ span: 12 }} lg={{ span: 12 }}>
                  <Form.Item label=" ">
                    <Button
                      className="btn-clean"
                      htmlType="reset"
                      onClick={(): void => {
                        cleanFilterFields();
                      }}
                    >
                      <FilterRemoveIcon />
                      Limpar
                    </Button>
                  </Form.Item>
                </Col>
                <Col xs={{ span: 12 }} lg={{ span: 12 }}>
                  <Form.Item label=" ">
                    <Button
                      className="btn-filter"
                      type="primary"
                      htmlType="submit"
                      // onClick={this.handleFilter}
                    >
                      <FilterIcon />
                      Filtrar
                    </Button>
                  </Form.Item>
                </Col>
              </Row>
            </Col>
          </Row>
          <Row gutter={24} className="filters-container" justify="start">
            <Col xs={{ span: 24 }} lg={{ span: 12 }}>
              <Form.Item label="Ação" name="action" rules={[]}>
                <Select
                  showSearch
                  allowClear
                  style={{ width: '100%' }}
                  placeholder="Todas"
                  filterOption={(input, option): any => option?.['props']?.children
                    .toLowerCase()
                    .includes(input.toLowerCase())}
                >
                  <Select.Option value="">Todas</Select.Option>
                  {actions.length
                    && actions.map(
                      ({ value, name }: LogActions, index: number) => (
                        <Select.Option
                          key={index}
                          className="select-option"
                          value={name}
                        >
                          {value}
                        </Select.Option>
                      ),
                    )}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={{ span: 24 }} lg={{ span: 6 }}>
              <Form.Item label=" ">
                <Button
                  className="add-new-btn"
                  type="primary"
                  onClick={exportExcel}
                  loading={loadingExcel}
                >
                  Exportar
                </Button>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24} className="list-container">
            <Col span={24}>
              <Row gutter={24}>
                <Col span={24}>
                  <Table
                    rowKey="actionDate"
                    columns={columns}
                    dataSource={data}
                    pagination={false}
                  />
                </Col>
              </Row>
              <Row gutter={24}>
                <Col span={24}>
                  <CustomPagination
                    paginationInfo={paginationInfo}
                    fetchFunc={fetchData}
                  />
                </Col>
              </Row>
            </Col>
          </Row>
        </Form>
      </Spin>
    </div>
  );
};

PageLogs.propTypes = {};

PageLogs.defaultProps = {};

export default PageLogs;
